import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface EditableCellProps {
  value: string | number | boolean;
  onSave: (newValue: string | number | boolean) => Promise<void>;
  type: "text" | "number" | "select" | "toggle";
  options?: { value: string; label: string; detail?: string; detailClassName?: string }[];
  displayValue?: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

const EditableCell = ({
  value,
  onSave,
  type,
  options,
  displayValue,
  className = "",
  disabled = false,
}: EditableCellProps) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editValue, setEditValue] = useState(String(value ?? ""));
  const inputRef = useRef<HTMLInputElement>(null);
  const closeTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  const stopEditing = () => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    // Wait for Radix close animation so the portal is gone before React unmounts Select.
    closeTimerRef.current = window.setTimeout(() => setEditing(false), 150);
  };

  const handleSave = async (newVal: string | number | boolean) => {
    if (newVal === value) {
      if (type !== "select") stopEditing();
      return;
    }
    setSaving(true);
    try {
      await onSave(newVal);
      if (type !== "select") stopEditing();
    } catch {
      toast.error("שגיאה בעדכון");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(String(value ?? ""));
    stopEditing();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const val = type === "number" ? Number(editValue) : editValue;
      handleSave(val);
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  // Toggle mode — always visible switch
  if (type === "toggle") {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {saving ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <Switch
            checked={Boolean(value)}
            disabled={disabled || saving}
            onCheckedChange={(checked) => handleSave(checked)}
          />
        )}
        {displayValue}
      </div>
    );
  }

  // Keep the Select mounted while saving. Replacing it with a spinner unmounts the
  // Radix portal while it is still attached to document.body (React 19 removeChild crash).
  if (editing && type === "select" && options) {
    return (
      <Select
        defaultOpen
        value={editValue || undefined}
        disabled={saving}
        onValueChange={(val) => {
          setEditValue(val);
          void handleSave(val);
        }}
        onOpenChange={(open) => {
          if (!open) stopEditing();
        }}
      >
        <SelectTrigger className="h-7 text-sm w-full py-0 px-1.5">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options
            .filter((opt) => opt.value !== "")
            .map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                <span className="flex items-center gap-2">
                  <span>{opt.label}</span>
                  {opt.detail && <span className={opt.detailClassName}>{opt.detail}</span>}
                </span>
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    );
  }

  // Saving state (text / number)
  if (saving) {
    return (
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Editing state
  if (editing) {
    return (
      <Input
        ref={inputRef}
        type={type === "number" ? "number" : "text"}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          const val = type === "number" ? Number(editValue) : editValue;
          handleSave(val);
        }}
        className="h-7 text-sm w-full py-0 px-1.5 border-muted"
        min={type === "number" ? 0 : undefined}
      />
    );
  }

  // Idle state — clickable display
  return (
    <div
      role={disabled ? undefined : "button"}
      tabIndex={disabled ? undefined : 0}
      onClick={() => {
        if (disabled) return;
        setEditValue(String(value ?? ""));
        setEditing(true);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !disabled) {
          setEditValue(String(value ?? ""));
          setEditing(true);
        }
      }}
      className={`rounded px-1 -mx-1 transition-colors min-h-[1.75rem] flex items-center ${disabled ? "cursor-default" : "cursor-pointer hover:bg-muted/50"} ${className}`}
    >
      {displayValue ?? <span>{String(value)}</span>}
    </div>
  );
};

export default EditableCell;
